import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { faCoins } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React from 'react';
import { OverlayTrigger, Spinner, Tooltip as BSTooltip } from 'react-bootstrap';
import Scan from 'src/components/scan/scan';
import Tooltip from 'src/components/Tooltip';
import UndockConfirmation from 'src/components/UndockConfirmation';
import { MLINE_ACTIONS_ALLOWED_RESOURCES, MLINE_MODULE_TYPES, MODULE_TYPES_MAP } from 'src/constants/m-line';
import DockBtn from 'src/pages/mline/sections/DockIcon';
import LPSBuildModule from 'src/pages/mline/sections/lps/modules/lps-build-module';
import LPSDoseModule from 'src/pages/mline/sections/lps/modules/lps-dose-module';
import LPSOverflowModule from 'src/pages/mline/sections/lps/modules/lps-overflow-module';
import UndockIcon from 'src/pages/mline/sections/UndockIcon';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { MLINE_ACTIONS } from 'src/utils/constants';
import { pluralWord } from 'src/utils/stringUtils';

const MODULE_COMPONENTS = {
  [MLINE_MODULE_TYPES.DOSE]: LPSDoseModule,
  [MLINE_MODULE_TYPES.BUILD]: LPSBuildModule,
  [MLINE_MODULE_TYPES.OVERFLOW]: LPSOverflowModule,
};

const LPSModulesContainer = ({
  modules,
  handleConnectModule,
  handleUndockModule,
  buildModuleMustBeUndocked,
  modulesLoadingState,
  batchTransactionsLoadingState,
  batchTransactions,
  unsupportedMaterialsForDose,
}) => {
  const {
    openActionPanel,
  } = useActionPanelStore();

  const {
    isDoseModuleLoading,
    isBuildModuleLoading,
    isOverflowModuleLoading,
  } = modulesLoadingState;

  const {
    isBatchTransactionsForBuildLoading,
    isBatchTransactionsForDoseLoading,
    isBatchTransactionsForOverflowLoading,
  } = batchTransactionsLoadingState;

  const {
    batchTransactionsForDose,
    batchTransactionsForBuild,
    batchTransactionsForOverflow,
  } = batchTransactions;

  const batchTransactionsByModuleType = {
    [MLINE_MODULE_TYPES.DOSE]: batchTransactionsForDose,
    [MLINE_MODULE_TYPES.BUILD]: batchTransactionsForBuild,
    [MLINE_MODULE_TYPES.OVERFLOW]: batchTransactionsForOverflow,
  }

  const batchTransactionsLoadingStateMap = {
    [MLINE_MODULE_TYPES.DOSE]: isBatchTransactionsForDoseLoading,
    [MLINE_MODULE_TYPES.BUILD]: isBatchTransactionsForBuildLoading,
    [MLINE_MODULE_TYPES.OVERFLOW]: isBatchTransactionsForOverflowLoading,
  }


  const modulesLoadingStateMap = {
    [MLINE_MODULE_TYPES.DOSE]: isDoseModuleLoading,
    [MLINE_MODULE_TYPES.BUILD]: isBuildModuleLoading,
    [MLINE_MODULE_TYPES.OVERFLOW]: isOverflowModuleLoading,
  }

  const handleDockModule = (moduleType) => {
    const moduleName = MODULE_TYPES_MAP[moduleType];
    const allowedResourcesToScan = MLINE_ACTIONS_ALLOWED_RESOURCES[moduleType];

    openActionPanel({
      title: `Scan ${moduleName}`,
      isSidebar: true,
      content: (<Scan
        action={MLINE_ACTIONS.DOCK_MODULE}
        allowedResourcesToScan={allowedResourcesToScan}
        handleAction={(moduleUri) => handleConnectModule(MLINE_ACTIONS.DOCK_MODULE, moduleUri, moduleType)} />
      ),
    });
  };

  const handleUndock = (module, moduleSlot) => {    
    const hasActiveTransactionsForModule = !_isEmpty(batchTransactionsByModuleType[module.type]) 
    && batchTransactionsByModuleType[module.type].some(transaction => !transaction.is_complete);
    openActionPanel({
      title: 'Undock Confirmation',
      content: <UndockConfirmation
        module={module}
        hasActiveTransactionsForModule={hasActiveTransactionsForModule}
        handleAction={() => handleUndockModule(MLINE_ACTIONS.UNDOCK_MODULE, module, moduleSlot)} />,
    });
  };

  const renderBatchTransactionsTooltip = (moduleType) => {
    const batchTransactionsLoading = batchTransactionsLoadingStateMap[moduleType];
    const currentBatchTransactions = batchTransactionsByModuleType[moduleType] || [];
    const batchHasIncompleteTransactions = currentBatchTransactions.some(transaction => !transaction.is_complete);

    if (batchTransactionsLoading)
      return (
        <Spinner
          animation='border'
          size='sm'
          variant='light'
          className='spacer-left mb0 align-self-center'
        />
      );

    if (!batchHasIncompleteTransactions) return null;

    return (
      <div className='spacer-left'>
        <Tooltip
          id='incomplete_transactions_tooltip'
          placement='bottom'
          trigger={
            <span className='powder_quality_badge'>
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                color='#FF9903'
                className='spacer-right'
              />
              <span className='fs-mobile-10'>Pending</span>
            </span>
          }
        >
          <div>Module has pending {pluralWord('transaction', currentBatchTransactions)}</div>
        </Tooltip>
      </div>
    );
  };

  const renderUnsupportedModuleMaterialsForDose = (moduleSlot) => {
    if (_isEmpty(unsupportedMaterialsForDose)) return null;
    const notSupportedMaterialsByModule = unsupportedMaterialsForDose[moduleSlot];

    const pluralMaterials = pluralWord('material', notSupportedMaterialsByModule);
    const pluralToBe = notSupportedMaterialsByModule?.length > 1 ? 'are' : 'is';

    if (notSupportedMaterialsByModule) {
      return (
        <Tooltip
          id='unsupported_dose_materials_tooltip'
          placement='right'
          trigger={
            <span>
              <FontAwesomeIcon
                color="#FFC008"
                icon={faTriangleExclamation}
                className="spacer-left"
              />
            </span>
          }
        >
          <div>
            This docked module does not support the following {pluralMaterials}&nbsp;
            <strong>{notSupportedMaterialsByModule.join(', ')}</strong> which {pluralToBe} loaded in the Dose module.
          </div>
        </Tooltip>
      )
    }
  }

  return (
    <div className="lps-modules-container">
      {modules.map(({ type, module }) => {
        // Retrieve the component associated with this module type
        const ModuleComponent = MODULE_COMPONENTS[type];
        if (!ModuleComponent) return null;

        const wrapperClassName = classNames('module-card mb25', {
          [`module-card-bg-${type}`]: !!module,
          'module-card-empty-bg': !module,
        });

        const isModuleEmpty = !module;

        return (
          <div key={type} className={wrapperClassName}>
            <div className="d-flex justify-content-between">
              <div className="d-flex align-items-base">
                <div>
                  <FontAwesomeIcon icon={faCoins} className="spacer-right module-title-size" />
                </div>
                <div className="text-left">
                  <div className="d-flex align-items-center">
                    <div className="module-title-size">{MODULE_TYPES_MAP[type]}</div>
                    {renderBatchTransactionsTooltip(type)}
                    {renderUnsupportedModuleMaterialsForDose(type)}
                  </div>

                  <div className="module-id">
                    <span>{isModuleEmpty ? 'Empty' : 'Docked'}</span>
                  </div>
                </div>
              </div>
              {modulesLoadingStateMap[type] ? <Spinner animation="border" /> : (
                <div className="d-flex align-items-center">
                  {type === MLINE_MODULE_TYPES.BUILD && buildModuleMustBeUndocked && module && (
                    <OverlayTrigger
                      placement="top"
                      overlay={(
                        <BSTooltip id="undock-module-info">
                          Module can be undocked
                        </BSTooltip>
                      )}
                    >
                      <FontAwesomeIcon size="2x" icon={faTriangleExclamation} color="#FFC008" className="spacer-right" />
                    </OverlayTrigger>
                  )}
                  {module ?
                    <UndockIcon onUndock={() => handleUndock(module, type)} /> :
                    <DockBtn onDock={() => handleDockModule(type)} />}
                </div>
              )}
            </div>
            <ModuleComponent module={module} isLoading={modulesLoadingStateMap[type]} />
          </div>
        );
      })}
    </div>
  );
};

LPSModulesContainer.propTypes = {
  modules: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      module: PropTypes.shape({}),
    }),
  ).isRequired,
  handleConnectModule: PropTypes.func.isRequired,
  handleUndockModule: PropTypes.func.isRequired,
  /* eslint-disable camelcase */
  buildModuleMustBeUndocked: PropTypes.bool.isRequired,
  modulesLoadingState: PropTypes.shape({
    isDoseModuleLoading: PropTypes.bool,
    isBuildModuleLoading: PropTypes.bool,
    isOverflowModuleLoading: PropTypes.bool,
  }).isRequired,
  batchTransactionsLoadingState: PropTypes.shape({
    isBatchTransactionsForBuildLoading: PropTypes.bool,
    isBatchTransactionsForDoseLoading: PropTypes.bool,
    isBatchTransactionsForOverflowLoading: PropTypes.bool,
  }).isRequired,
  batchTransactions: PropTypes.shape({
    batchTransactionsForBuild: PropTypes.array,
    batchTransactionsForDose: PropTypes.array,
    batchTransactionsForOverflow: PropTypes.array,
  }).isRequired,
  unsupportedMaterialsForDose: PropTypes.shape({}).isRequired,
};

export default LPSModulesContainer;
